import { useAppTheme } from '@droplet-tech-code/core-elements/module/theme';
import { DelayRender } from '@droplet-tech-code/core-elements/module/ui/Delay';
import { DottedLine } from '@droplet-tech-code/core-elements/module/ui/Divider/Divider';
import { FormHandler } from '@droplet-tech-code/core-elements/module/ui/Inputs/Form';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { ScrollView, View } from '@droplet-tech-code/core-elements/module/ui/View';
import {
  isNativeMobile,
  isWeb,
} from '@droplet-tech-code/core-elements/module/utils/utils.helper';
import {
  PurchaseClientWithMetaWithTickets,
  SupportRequestBackendClient,
} from '@naus-code/naus-admin-types';
import { BasketManagerErrorRes } from '@naus-code/naus-client-types';
import React, { useRef } from 'react';

import { BookingInfo } from '~/components/Bookings/BookingInfo';
import { CardWithHeader } from '~/components/Card/CardWithHeader';
import { JSONView, useJsonViewerModal } from '~/components/JsonView';
import { PageContainer } from '~/components/Page/Page.Container';
import { SwitchStack } from '~/components/Stack/SwitchStack';
import { SupportConversationInputPanel } from '~/components/SupportConversation/InputPanel/Conversation.InputPanel';
import { chatCache } from '~/components/SupportConversation/SupportConversation.utils';
import {
  SupportRequestConversation,
  SupportRequestConversationPreview,
} from '~/components/SupportConversation/SupportRequestConversation';
import { SupportRequestAccordionComponent } from '~/components/SupportRequest/SupportRequestAccordion';
import { useAttachParamsToUrl } from '~/hooks/nav.hook';
import {
  RootStackParamList,
  ScreenPropSupportRequestItem,
} from '~/navigation/Navigation.types';
import { translate } from '~/utils/translation.utils';

import { bookingApi } from '../Bookings/Bookings.api';
import { PurchaseItem } from '../Purchases/PurchaseItem';
import {
  SupportRequestItemHeader,
  SupportRequestItemHeaderSubTitle,
} from './SupportRequest.ItemHeader';
import { supportRequestApi } from './SupportRequests.api';
import { Pill } from '~/components/Pill/Pill';
import { AppText } from '~/components/Text';
import { Table } from '~/components/Table/Table';
import { onReceiveValueWithUndefined } from '~/components/SupportRequest/utils/utils';
import { Icon } from '@droplet-tech-code/core-elements/module/ui/Icon';
import { Pressable } from 'react-native';
import { supportRequestActionsApi } from './supportRequestActions.api';
import { purchaseApi } from '../Purchases/Purchases.api';
import { LoadingIndicator } from '@droplet-tech-code/core-elements/module/ui/Loading';
import { Field } from '~/components/Card/Field';

export const SupportRequestItemScreen = (props: ScreenPropSupportRequestItem) => {
  return <SupportRequestItemNative id={props.route.params.id} />;
};

export const SupportRequestItemNative = ({ id }: { id: string }) => {
  const { data, isLoading } = supportRequestApi.useGetSupportRequestQuery(id ?? '', {
    skip: !id,
  });

  if (data === undefined) {
    return null;
  }
  const check = data.check;
  const bookingId = data.supportRequest.bookingId;
  const currentPurchaseId = data.supportRequest.purchaseId;
  const conversation = data.supportRequest.conversation;

  return (
    <DelayRender isLoading={isLoading}>
      <VStack flex={1}>
        <SupportRequestItemHeader supportRequest={data.supportRequest} check={check} />
        <ScrollView>
          <VStack p="2.5">
            {data ? (
              <CardWithHeader headerText={translate('supportRequests.details')} my="2">
                <View p="2">
                  <SupportRequestItemHeaderSubTitle
                    email={data.supportRequest.p_email}
                    reference={data.supportRequest.ref}
                    dateCreated={data.supportRequest.dateCreated}
                  />
                </View>
              </CardWithHeader>
            ) : null}
            <VStack space="2.5" py="2.5">
              <BookingInfo
                bookingId={bookingId}
                supportId={data.supportRequest._id}
                isPrioritySupport={
                  (data.supportRequest as { isPrioritySupport?: boolean })
                    .isPrioritySupport
                }
              />
              <SupportRequestPurchasesComponent
                currentPurchaseId={currentPurchaseId}
                bookingId={bookingId}
                supportId={data.supportRequest._id}
              />
            </VStack>
            <VStack py="2.5">
              <SupportRequestConversationPreview
                supportRequestId={id}
                check={check}
                supportConversation={conversation}
              />
            </VStack>
          </VStack>
        </ScrollView>
      </VStack>
    </DelayRender>
  );
};

export const SupportRequestItem = ({
  originScreen,
  supportId,
  onClose,
}: {
  originScreen?: keyof RootStackParamList;
  supportId: string;
  onClose?: () => void;
}) => {
  useAttachParamsToUrl(originScreen, { id: supportId });

  const { data } = supportRequestApi.useGetSupportRequestQuery(supportId ?? '', {
    skip: !supportId,
    refetchOnMountOrArgChange: !!supportId,
  });

  const { spacing } = useAppTheme();
  const advancedRef = useRef(new FormHandler());
  const textRef = useRef(new FormHandler());

  const cachedInitText = chatCache.cache[supportId];

  if (data === undefined) {
    return null;
  }

  const check = data.check;
  const bookingId = data?.supportRequest.bookingId;
  const currentPurchaseId = data?.supportRequest.purchaseId;
  const conversation = data?.supportRequest.conversation;
  const basketError = data?.supportRequest.basketError;

  const isZD = data?.supportRequest.zdData;

  return (
    <>
      <SupportRequestItemHeader
        supportRequest={data.supportRequest}
        check={data.check}
        onClose={onClose}
      />
      <PageContainer bg="monochrome-base">
        <SwitchStack
          flex={1}
          space="2.5"
          style={{ alignSelf: 'stretch', alignItems: 'flex-start' }}
        >
          <VStack style={{ height: '100%' }}>
            <ScrollView
              contentContainerStyle={{
                flexGrow: 1,
                paddingHorizontal: spacing[1],
              }}
            >
              <VStack space="2.5" py="2.5">
                {basketError ? <BasketErrorInfo basketError={basketError} /> : null}
                <BookingInfo
                  bookingId={bookingId}
                  supportId={supportId}
                  isPrioritySupport={
                    (data.supportRequest as { isPrioritySupport?: boolean })
                      .isPrioritySupport
                  }
                />
                <PaymentSummary bookingId={bookingId} />
                <AutoAssignedCard supportRequest={data.supportRequest} check={check} />
                <KoalaCard bookingId={data.supportRequest.bookingId} />
                <CfarCard bookingId={data.supportRequest.bookingId} />
                <SupportRequestPurchasesComponent
                  currentPurchaseId={currentPurchaseId}
                  bookingId={bookingId}
                  supportId={supportId}
                />
              </VStack>
            </ScrollView>
          </VStack>
          {isZD ? null : (
            <VStack
              flex={1}
              py="2.5"
              style={[
                { height: '100%', alignItems: 'center' },
                {
                  minWidth: 700,
                },
              ]}
              space="2.5"
            >
              <SupportRequestConversation
                supportRequestId={supportId}
                check={check}
                supportConversation={conversation}
              />
              <SupportConversationInputPanel
                supportId={supportId}
                initialText={cachedInitText}
                key={supportId}
                supportRequestRes={data}
                advancedRef={advancedRef}
                textRef={textRef}
              />
            </VStack>
          )}
          <VStack
            style={{ width: 400, height: '100%', borderLeftWidth: 1 }}
            bc="monochrome-extraLight"
          >
            <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
              <SupportRequestAccordionComponent data={data} />
            </ScrollView>
          </VStack>
        </SwitchStack>
      </PageContainer>
    </>
  );
};

export const SupportRequestPurchasesComponent = ({
  currentPurchaseId,
  bookingId,
  supportId,
}: {
  currentPurchaseId?: string;
  bookingId?: string;
  supportId?: string;
}) => {
  const { currentData, data, isError } = bookingApi.useGetBookingQuery(bookingId ?? '', {
    skip: !bookingId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });
  const bookingInfo = data?.booking;
  const purchaseListItems =
    bookingInfo?.purchases ?? ([] as PurchaseClientWithMetaWithTickets[]);

  if (currentPurchaseId === undefined) {
    return null;
  }
  if (bookingId === undefined) {
    return null;
  }

  if (bookingInfo === undefined || !bookingId || (!currentData && isError)) {
    return (
      <CardWithHeader
        headerText={`${translate('purchases.purchases')} (0)`}
        style={{ minWidth: isWeb ? 600 : undefined }}
      ></CardWithHeader>
    );
  }
  return (
    <CardWithHeader
      w={isNativeMobile ? undefined : 600}
      headerText={`${translate('purchases.purchases')} (${purchaseListItems.length})`}
      rightChild={() => {
        if (bookingInfo.bookingProtection) {
          return (
            <Pill color="error-mid" bg="error-base">
              <AppText.Body2Medium color="error">{'CFAR'}</AppText.Body2Medium>
            </Pill>
          );
        }

        if (
          bookingInfo.bookingKoala &&
          bookingInfo.bookingKoala?.status !== 'PENDING_DELETE'
        ) {
          return (
            <Pill color="primary-mid" bg="primary-base">
              <AppText.Body2Medium color="primary">{'KOALA'}</AppText.Body2Medium>
            </Pill>
          );
        }

        return <></>;
      }}
    >
      <VStack space="2.5">
        {purchaseListItems.map((purchase, ix) => {
          return (
            <SupportRequestPurchaseComponent
              key={purchase._id}
              index={ix}
              purchase={purchase}
              purchaseListItemsLength={purchaseListItems.length}
              supportId={supportId}
              currentPurchaseId={currentPurchaseId}
            />
          );
        })}
      </VStack>
    </CardWithHeader>
  );
};

export const AutoAssignedCard = ({
  supportRequest,
  check,
}: {
  supportRequest: SupportRequestBackendClient;
  check: string;
}) => {
  const [changeSelection, { isLoading: isAssigning }] =
    supportRequestActionsApi.useChangeSelectionReferencesMutation();
  const { data: purchaseData } = purchaseApi.useGetPurchaseQuery(
    supportRequest.purchaseId || '',
    { skip: !supportRequest.purchaseId },
  );

  if (!supportRequest.bookingId || !supportRequest.purchaseId || !purchaseData) {
    return null;
  }

  if (
    supportRequest.supportHistory.some(
      (history) => history.type === 'SELECTION_REF_CHANGES',
    )
  ) {
    return null;
  }

  if (
    supportRequest.supportHistory.some((history) => history.type === 'PURCHASE_ASSIGN')
  ) {
    return (
      <Pressable
        onPress={() =>
          changeSelection({
            bookingId: supportRequest.bookingId!,
            purchaseId: supportRequest.purchaseId!,
            check: check,
            ticketIds: purchaseData.purchase._ticketIds,
            supportId: supportRequest._id,
          })
        }
      >
        <VStack flex={1} align="center" bg="warning-extraLight" br={10} p="1.5">
          <HStack space="1.5">
            <AppText.MiscTag color="warning-extraDark">
              {translate('supportRequests.purchaseAttachedAutomatically')}
            </AppText.MiscTag>
            <Icon icon="warning" color="error" />
            {isAssigning ? <LoadingIndicator /> : null}
          </HStack>
        </VStack>
      </Pressable>
    );
  }
};

export const KoalaCard = ({ bookingId }: { bookingId?: string }) => {
  const { data } = bookingApi.useGetBookingQuery(bookingId || '', {
    skip: !bookingId,
  });
  const koalaData = data?.booking.bookingKoala;
  if (!koalaData || !bookingId || koalaData?.status === 'PENDING_DELETE') {
    return null;
  }

  return (
    <VStack flex={1} bg="primary-extraLight" br={10} p="1.5" w={600}>
      <HStack space="1.5">
        <Icon icon="insurance" color="primary" />
        <AppText.Body1Medium color="primary-extraDark" style={{ marginTop: 2 }}>
          {translate('supportRequests.koalaDetails')}
        </AppText.Body1Medium>
      </HStack>
      <HStack p="2.5" space="3">
        <Field
          label={translate('supportRequests.koala.status')}
          labelType="small"
          value={koalaData.status}
          valueType="small"
        />
        <Field
          label={translate('supportRequests.koala.name')}
          labelType="small"
          value={koalaData.name}
          valueType="small"
        />
      </HStack>
      <HStack p="2.5" space="3">
        <Field
          flex={2}
          label={translate('supportRequests.koala.timestamp')}
          labelType="small"
          value={koalaData.timestamp}
          valueType="small"
          isDate
        />
        <Field
          flex={2}
          label={translate('supportRequests.koala.refundValue')}
          labelType="small"
          value={onReceiveValueWithUndefined(koalaData.refundValue) + ''}
          valueType="small"
        />
      </HStack>
      <HStack p="2.5" space="3">
        <Field
          flex={2}
          label={translate('supportRequests.koala.numberOfModifications')}
          labelType="small"
          value={koalaData.numberOfModifications + ''}
          valueType="small"
        />
        <Field
          flex={2}
          label={translate('supportRequests.koala.numberOfCancellations')}
          labelType="small"
          value={koalaData.numberOfCancellations + ''}
          valueType="small"
        />
      </HStack>
    </VStack>
  );
};

export const CfarCard = ({ bookingId }: { bookingId?: string }) => {
  const { data } = bookingApi.useGetBookingQuery(bookingId || '', {
    skip: !bookingId,
  });
  const { open: openJsonViewer } = useJsonViewerModal();

  const cfarData = data?.booking.bookingProtection;
  console.log(cfarData);
  if (!cfarData || !bookingId) {
    return null;
  }

  return (
    <VStack flex={1} bg="error-base" br={10} p="1.5" w={600}>
      <HStack space="1.5">
        <Icon icon="insurance" color="error" />
        <AppText.Body1Medium color="error-extraDark" style={{ marginTop: 2 }}>
          {translate('supportRequests.cfarDetails')}
        </AppText.Body1Medium>
      </HStack>

      <HStack p="2.5" space="3">
        <Field
          label={translate('supportRequests.koala.status')}
          labelType="small"
          value={cfarData.numberOfCancellations > 1 ? 'Invalid' : 'Valid'}
          valueType="small"
        />
        <Field
          label={translate('supportRequests.koala.name')}
          labelType="small"
          value={cfarData.refundPercentage * 100 + '%'}
          valueType="small"
        />
      </HStack>
      <HStack p="2.5" space="3">
        <Field
          flex={2}
          label={translate('supportRequests.cfar.validUntil')}
          labelType="small"
          value={cfarData.refundValidUntil}
          valueType="small"
          isDate
        />
        {cfarData.refundValue && (
          <Field
            flex={2}
            label={translate('supportRequests.koala.refundValue')}
            labelType="small"
            value={onReceiveValueWithUndefined(cfarData.refundValue) + ''}
            valueType="small"
          />
        )}
      </HStack>
      <HStack p="2.5" space="3">
        <Field
          flex={2}
          label={translate('supportRequests.koala.numberOfModifications')}
          labelType="small"
          value={cfarData.numberOfModifications + ''}
          valueType="small"
        />
        <Field
          flex={2}
          label={translate('supportRequests.koala.numberOfCancellations')}
          labelType="small"
          value={cfarData.numberOfCancellations + ''}
          valueType="small"
        />
      </HStack>
      <HStack p="2.5" space="3">
        <Pressable
          onPress={() =>
            openJsonViewer({
              title: translate('supportRequests.entries'),
              data: cfarData.entries,
            })
          }
        >
          <AppText.MiscTag color="primary-extraDark">{`${translate('supportRequests.entries')} ${cfarData.entries.length}`}</AppText.MiscTag>
        </Pressable>
      </HStack>
    </VStack>
  );
};

const SupportRequestPurchaseComponent = ({
  index,
  purchaseListItemsLength,
  purchase,
  supportId,
  currentPurchaseId,
}: {
  index: number;
  purchaseListItemsLength: number;
  purchase: PurchaseClientWithMetaWithTickets;
  supportId?: string;
  currentPurchaseId: string;
}) => {
  const purchaseId = purchase._id;
  if (currentPurchaseId === purchaseId) {
    return (
      <>
        <VStack p="2.5">
          <PurchaseItem
            purchaseId={currentPurchaseId}
            supportId={supportId}
            withActions
          />
        </VStack>
        {purchaseListItemsLength - 1 !== index ? <DottedLine /> : null}
      </>
    );
  }

  return (
    <>
      <View
        style={[
          { opacity: 0.5 },
          purchase.split && {
            opacity: 1,
          },
        ]}
        p="2.5"
      >
        <PurchaseItem purchaseId={purchase._id} supportId={supportId} withActions />
      </View>
      {purchaseListItemsLength - 1 !== index ? <DottedLine /> : null}
    </>
  );
};

export const BasketErrorInfo = ({
  basketError,
}: {
  basketError: BasketManagerErrorRes;
}) => {
  return (
    <CardWithHeader headerText="Basket Error" style={[isWeb && { width: 400 }]}>
      <SwitchStack p="2.5" space={isNativeMobile ? '1' : '2.5'}>
        <VStack flex={isNativeMobile ? undefined : 1} space="1">
          <JSONView data={basketError} />
        </VStack>
      </SwitchStack>
    </CardWithHeader>
  );
};

export const PaymentSummary = ({ bookingId }: { bookingId?: string }) => {
  const { data } = bookingApi.useGetBookingQuery(bookingId || '');

  if (!bookingId) {
    return null;
  }

  if (!data) {
    return null;
  }

  let voucherValue = false;
  const paymentSummaries: (string | number | undefined)[][] =
    data.booking.paymentSessions.map((payment) => {
      if (!!payment.paymentSummary.voucher?.voucherValue) {
        voucherValue = true;
      }

      // `prioritySupport` is part of the new "Priority Support" product and is not
      // yet present on the packaged type — read it via a cast until the types land.
      const prioritySupport = (
        payment.paymentSummary as typeof payment.paymentSummary & {
          prioritySupport?: { valueInBookCcy?: number };
        }
      ).prioritySupport;

      return [
        payment.type,
        payment.status.status,
        payment.paymentSummary.currency,
        voucherValue
          ? onReceiveValueWithUndefined(payment.paymentSummary.voucher?.voucherValue)
          : undefined,
        onReceiveValueWithUndefined(payment.paymentSummary.total),
        onReceiveValueWithUndefined(
          payment.paymentSummary.tripsValue?.reduce(
            (cache, trip) => (cache += trip.value),
            0,
          ),
        ),
        payment.type === 'booking'
          ? onReceiveValueWithUndefined(payment.paymentSummary.serviceFee?.valueInBookCcy)
          : undefined,
        payment.type !== 'booking'
          ? onReceiveValueWithUndefined(payment.paymentSummary.serviceFee?.valueInBookCcy)
          : undefined,
        onReceiveValueWithUndefined(
          payment.paymentSummary?.bookingProtection?.valueInBookCcy,
        ),
        onReceiveValueWithUndefined(payment.paymentSummary?.bookingKoala?.valueInBookCcy),
        onReceiveValueWithUndefined(payment.paymentSummary?.smsExtra?.valueInBookCcy),
        onReceiveValueWithUndefined(prioritySupport?.valueInBookCcy),
      ];
    });

  const headers = [
    'Type',
    'Status',
    'Ccy',
    'Voucher value',
    'Total',
    'Trips value',
    'Book fee',
    'Service fee',
    'CFAR',
    'Koala',
    'SMS',
    'Priority',
  ];
  return (
    <CardWithHeader
      iconRightType={voucherValue ? 'alert-triangle' : undefined}
      iconRightColor="warning"
      headerText={translate('supportRequests.paymentSummary')}
      w={600}
    >
      <Table headers={headers} data={paymentSummaries} />
    </CardWithHeader>
  );
};
